import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import EmailField from 'components/EmailField';
import useResendVerificationMail from 'hooks/useResendVerificationMail';
import { User } from 'models/User';

import './EmailMissingModal.styles.scss';

type EmailMissingModalProps = {
  className?: string;
  listingId?: string;
  onUserUpdate: (user: User) => void;
} & ModalProps;

const EmailMissingModal: React.ForwardRefRenderFunction<
  ModalRef,
  EmailMissingModalProps
> = (props, ref) => {
  const { className, listingId, onUserUpdate, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('email-missing-modal', className);

  const { t } = useTranslation();

  const { resendVerificationMail } = useResendVerificationMail();

  const onSuccess = useCallback(
    (user: User) => {
      modal?.close();
      onUserUpdate(user);
    },
    [modal, onUserUpdate],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('verifyYourEmail')}
      {...rest}
    >
      <Form
        onSubmit={({ email }: { email: string }) =>
          resendVerificationMail({ email, listingId }, onSuccess)
        }
        render={({ submitting, invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}

            <p>{t('verifyYourEmailModalInfo')}</p>

            <EmailField required />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('sendVerificationLink')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(EmailMissingModal);
